import { parse } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { analyticsStoreEvent } from "@/mk2/actions";
import { ListOfForumsRight } from "@/mk2/apps/forum/containers/ListOfForums/ListOfForumsRight";
import { topicUrl } from "@/mk2/apps/forum/helpers";
import { CategoryEntity, TopicEntity, TopicSchema } from "@/mk2/apps/forum/schemas";
import { forumListOfForumsUrl, forumTopicNewUrl } from "@/mk2/apps/forum/urls";
import { groupsListUrl } from "@/mk2/apps/groups/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { AppName } from "@/mk2/containers/Application/Application";
import { memoizeOne } from "@/mk2/helpers/cache";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, {
    AnchorLeaderboardProps,
    BrandingProps,
    HalfpageProps,
    InterstitialProps,
    PageRouteProps,
    RightContentSide,
} from "@/mk2/pages/Page";
import {
    AppState,
    getRequestBaseUrl,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import { MenuSelection, UserEntity } from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import {
    FORUM_CREATE_TOPIC,
    GROUP_LIST,
    LIST_OF_FORUMS,
    SEO_DESCRIPTION_FORUM_category,
    SEO_TITLE_FORUM_TOPICS,
} from "@/mk/autogenerated/translation";
import { hasAds } from "@/mk/bazaar/common/userUtils";

import ListOfForums from "./ListOfForums";
import { listOfForumsLoad } from "./ListOfForums.actions";
import listOfForumsPageReducer, { TopicsListState } from "./ListOfForums.reducers";
import listOfForumsSaga from "./ListOfForums.sagas";
import { getListOfForumsTopicsListState } from "./ListOfForums.selectors";

type ItemType = "category" | "common-place" | "topic";

interface Item {
    type: ItemType;
    topicId: number;
    category?: CategoryEntity;
    topic?: TopicEntity;
}

interface OwnProps {
    // You can change list item to="" link
    itemToTopicUrl(item: TopicEntity);
}

interface StateProps {
    after: number;
    baseUrl: string;
    brandDaysRowNo: number;
    errorMessage: string;
    hasNext: number;
    hasPrevious: number;
    items: Item[];
    loadingState: LoadingState;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    isMobile: boolean;
    systemCatRowNo: number;
}

interface DispatchProps {
    onLoad(username: string, after?: number);
    onStoreEvent(name: string, props: any);
}

const topicsToItems = memoizeOne((topics: TopicEntity[]): Item[] => {
    let lastCategoryId = null;
    return topics.reduce((acc, topic, idx) => {
        if (idx === 0) {
            acc.push({
                type: "common-place",
            });
        }

        // category heading
        if (topic.category.id !== lastCategoryId) {
            lastCategoryId = topic.category.id;
            acc.push({
                type: "category",
                category: topic.category,
                topicId: topic.id,
            });
        }

        // topic
        acc.push({
            type: "topic",
            topic,
            topicId: topic.id,
        });
        return acc;
    }, []);
});

type RouteProps = PageRouteProps<{}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class ListOfForumsPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const parsed = parse(ownProps.location.search, { parseNumbers: true });
            const after = parsed.after as number;

            const listState: TopicsListState = getListOfForumsTopicsListState(state);

            const topics = getDenormalizedEntities<TopicEntity>(state, TopicSchema, listState.ids);
            const hasNext = listState.hasNext;

            const requestUser = getRequestUser(state);
            const requestUserPermissions = getRequestPermissions(state);

            const items = topicsToItems(topics);

            const systemCatRowNo =
                !hasNext && isSuccess(listState.loadingState) ? items.length : null;
            const brandDaysRowNo = systemCatRowNo !== null ? systemCatRowNo + 1 : null;

            return {
                after,
                baseUrl: getRequestBaseUrl(state),
                brandDaysRowNo,
                errorMessage: listState.errorMessage,
                hasNext,
                hasPrevious: listState.hasPrevious,
                items,
                loadingState: listState.loadingState,
                requestUser,
                requestUserPermissions,
                isMobile: getRequestDeviceMobile(state),
                systemCatRowNo,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: listOfForumsLoad,
            onStoreEvent: analyticsStoreEvent,
        };
    }

    public getMetaTitle(props: Props): string {
        return SEO_TITLE_FORUM_TOPICS;
    }

    public getMetaDescription(props: Props): string {
        return interpolate(SEO_DESCRIPTION_FORUM_category, { category: "..." });
    }

    public getCanonicalUrl(baseUrl: string, props: Props): string {
        return baseUrl + url(forumListOfForumsUrl, {});
    }

    public render(props: Props): false | JSX.Element {
        return <ListOfForums {...props} itemToTopicUrl={this.itemToTopic} />;
    }

    public renderRight(props: Props): JSX.Element {
        return <ListOfForumsRight {...props} />;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.LeftSide;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return false;
    }

    public getHeaderTabs(props: Props): JSX.Element[] | null {
        return [
            <Btn
                key="forums"
                type={BtnType.Tab}
                label={LIST_OF_FORUMS}
                link={{
                    pathname: forumListOfForumsUrl,
                    search: props.location.search,
                }}
                active
            />,
            <Btn key="groups" type={BtnType.Tab} label={GROUP_LIST} link={groupsListUrl} />,
        ];
    }

    public hasMobileLogo(props: Props): boolean {
        return true;
    }

    public getHeaderRight(props: Props): React.ReactElement {
        const { requestUserPermissions } = props;
        const isAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") >= 0;

        return (
            isAdmin && (
                <Btn
                    type={BtnType.AdminOutline}
                    label={FORUM_CREATE_TOPIC}
                    link={forumTopicNewUrl}
                />
            )
        );
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Forum Category - Mid 01 - Desktop",
        };
    }

    public getBrandingProps(props: Props): BrandingProps {
        return {
            zone: props.isMobile
                ? "Forum Category - Top 01 - Mobile"
                : "Forum Category - Top 01 - Desktop",
        };
    }

    public getInterstitialProps(props: Props): InterstitialProps | null {
        return { zone: "Forum Category - Interstitial" };
    }

    public getAnchorLeaderboardProps(props: Props): AnchorLeaderboardProps {
        const { requestUser, isMobile, items } = props;

        if (!isMobile || (requestUser && !hasAds(requestUser))) {
            return null;
        }

        if ((items?.length || 0) < 20) {
            return null;
        }

        return {
            zone: "Forum Category - Anchor - Mobile",
        };
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: "forum",
            activeItem: "list",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.forum.listOfForums", listOfForumsPageReducer);
        store.injectSaga("containers.forum.listOfForums", listOfForumsSaga);
    }

    private itemToTopic = (item: TopicEntity) => topicUrl(item);
}
